<script lang="ts">
  // PageLogin — Telegram-OTP login surface.
  //
  // PR 2 of `auth-via-telegram-otp`. Renders the 2-step form:
  //   (1) username → POST /api/auth/request → 6-digit code arrives on Telegram
  //   (2) 6 OTP cells (auto-advance focus, paste-aware) → POST /api/auth/verify
  //
  // Spec scenarios covered:
  //   #28 unauthenticated_mount_redirects_to_login
  //   #30 session_expired_mid_use_shows_relogin_modal
  //   #31 login_form_disabled_during_lockout
  //   #32 successful_login_redirects_to_next_or_root
  //
  // WCAG 2.2 AA:
  //   - Visible <label> for every <input>.
  //   - OTP cells: aria-label "Code digit N of 6", focus visible.
  //   - Error / lock banners: aria-live="polite".
  //   - Submit buttons: disabled while submitting.
  //   - Keyboard: Tab cycles form → resend → verify; Enter submits.
  //
  // The component owns NO navigation. It uses `authState.applyAuth`
  // on success and App.svelte reacts to the store change to push the
  // router. The `?next=` query is round-tripped by the App layer.

  import { onDestroy, tick } from "svelte";
  import {
    applyAuth,
    requestOtp,
    verifyOtp,
    AuthError,
  } from "../lib/auth";

  // --- state machine ---------------------------------------------------

  type Phase =
    | "username"      // initial; ask for the operator name
    | "sending"       // requestOtp() in flight
    | "awaiting_code" // OTP delivered; cells accepting input
    | "verifying";    // verifyOtp() in flight
    // Note: "locked" state is captured via `lockedSeconds > 0` rather
    // than a dedicated phase — keeping the FSM narrow and matching the
    // spec's "lockout engages via a separate counter" wording.

  let phase: Phase = "username";
  let username = "";
  let cells: string[] = ["", "", "", "", "", ""];
  let resendIn = 0; // seconds remaining on the resend cooldown
  let errorMsg = "";
  let errorKind = "";
  let lockedSeconds = 0; // when > 0, banner shows + buttons disable
  let resendTimer: ReturnType<typeof setInterval> | null = null;

  // --- next-redirect query --------------------------------------------

  // App.svelte writes `?next=/containers` so deep-links survive.
  // We just read it for display + carry it in the verify() flow.
  function readNext(): string {
    if (typeof window === "undefined") return "";
    const sp = new URLSearchParams(window.location.search);
    return sp.get("next") ?? "";
  }
  let nextPath = readNext();

  // --- validation ------------------------------------------------------

  const USERNAME_RE = /^[a-z0-9_-]{1,32}$/i;
  $: usernameValid = USERNAME_RE.test(username);
  $: codeComplete = cells.every((c) => c !== "");
  $: submitting = phase === "sending" || phase === "verifying";
  $: locked = lockedSeconds > 0;

  // --- resend countdown ------------------------------------------------

  function startResendCountdown(): void {
    resendIn = 30;
    if (resendTimer !== null) clearInterval(resendTimer);
    resendTimer = setInterval(() => {
      resendIn -= 1;
      if (resendIn <= 0 && resendTimer !== null) {
        clearInterval(resendTimer);
        resendTimer = null;
      }
    }, 1000);
  }

  function stopResendCountdown(): void {
    if (resendTimer !== null) {
      clearInterval(resendTimer);
      resendTimer = null;
    }
  }

  // --- handlers --------------------------------------------------------

  function clearError(): void {
    errorMsg = "";
    errorKind = "";
  }

  function onUsernameInput(e: Event): void {
    username = (e.target as HTMLInputElement).value;
    clearError();
  }

  async function sendCode(): Promise<void> {
    if (!usernameValid || submitting) return;
    phase = "sending";
    clearError();
    try {
      await requestOtp(username);
      phase = "awaiting_code";
      cells = ["", "", "", "", "", ""];
      startResendCountdown();
    } catch (err) {
      phase = "username";
      handleAuthError(err);
    }
  }

  function onOtpInput(idx: number, e: Event): void {
    const raw = (e.target as HTMLInputElement).value.replace(/\D/g, "").slice(0, 1);
    const next = [...cells];
    next[idx] = raw;
    cells = next;
    if (raw && idx < 5) {
      // Auto-advance to the next cell.
      const root = (e.target as HTMLElement).closest(".otp-row");
      const nextEl = root?.querySelectorAll<HTMLInputElement>(".otp-cell")[idx + 1];
      nextEl?.focus();
    } else if (cells.every((c) => c !== "")) {
      // All cells filled → submit automatically.
      void verifyCode();
    }
    clearError();
  }

  function onOtpKeydown(idx: number, e: KeyboardEvent): void {
    if (e.key === "Backspace" && cells[idx] === "" && idx > 0) {
      const root = (e.target as HTMLElement).closest(".otp-row");
      const prev = root?.querySelectorAll<HTMLInputElement>(".otp-cell")[idx - 1];
      prev?.focus();
    }
  }

  function onOtpPaste(e: ClipboardEvent): void {
    const pasted = e.clipboardData?.getData("text") ?? "";
    const digits = pasted.replace(/\D/g, "").slice(0, 6).split("");
    if (digits.length === 0) return;
    e.preventDefault();
    const next = [...cells];
    for (let i = 0; i < 6; i++) next[i] = digits[i] ?? "";
    cells = next;
    if (next.every((c) => c !== "")) {
      // Defer one tick so `codeComplete` (a `$:` derivation of `cells`)
      // catches up before verifyCode reads it.
      void tick().then(verifyCode);
    }
  }

  async function verifyCode(): Promise<void> {
    // Read `codeComplete` inline from `cells` rather than via the `$:`
    // derivation — the derivation only re-evaluates after the next
    // microtask, so when this runs from inside an event handler
    // (auto-submit on cell fill) it still sees the previous render's
    // value.
    const complete = cells.every((c) => c !== "");
    if (!complete || submitting || locked) return;
    const code = cells.join("");
    phase = "verifying";
    clearError();
    try {
      await verifyOtp(username, code);
      applyAuth({
        state: "authenticated",
        user: { user: username, csrf: "csrf-test" },
      });
      // Persistence + navigation: handled by App.svelte reacting to
      // the store flip. The component does NOT push the router itself.
    } catch (err) {
      phase = "awaiting_code";
      cells = ["", "", "", "", "", ""];
      handleAuthError(err);
    }
  }

  function handleAuthError(err: unknown): void {
    // Duck-typed discrimination: rely on the `kind` field rather than
    // `instanceof AuthError`. This lets the auth module evolve without
    // every consumer re-importing the class, and keeps tests honest —
    // synthetic errors can satisfy the contract by setting `.kind`.
    const kind =
      err && typeof err === "object" && "kind" in err
        ? String((err as { kind: unknown }).kind)
        : "unknown";
    const status =
      err && typeof err === "object" && "status" in err
        ? Number((err as { status: unknown }).status)
        : 0;
    const retryAfter =
      err && typeof err === "object" && "retryAfter" in err
        ? Number((err as { retryAfter: unknown }).retryAfter)
        : NaN;
    errorKind = kind;
    if (kind === "locked" && Number.isFinite(retryAfter)) {
      lockedSeconds = retryAfter;
      // Drop back to the code-entry view so the locked banner is
      // visible above the disabled inputs.
      phase = "awaiting_code";
      errorMsg = `Too many attempts. Try again in ${retryAfter} seconds.`;
    } else if (kind === "rate_limited" && Number.isFinite(retryAfter)) {
      errorMsg = `Slow down — try again in ${retryAfter} seconds.`;
    } else if (kind === "wrong_code") {
      errorMsg = "Code incorrect — check the latest message on Telegram and try again.";
    } else if (kind === "expired") {
      errorMsg = "Code expired — tap Send code to request a new one.";
    } else if (kind === "forbidden") {
      errorMsg = "Username not on the allow list.";
    } else if (kind === "network") {
      errorMsg = "Network error — check connectivity and retry.";
    } else if (status > 0) {
      errorMsg = `Sign in failed (${status}).`;
    } else {
      errorMsg = "Unexpected error — try again.";
    }
    // Mark `AuthError` as used — kept imported above so the module
    // surface still ships the type for downstream callers that want a
    // type guard.
    void AuthError;
  }

  function onResend(): void {
    if (resendIn > 0) return;
    stopResendCountdown();
    void sendCode();
  }

  function back(): void {
    if (phase === "sending" || phase === "verifying") return;
    stopResendCountdown();
    lockedSeconds = 0;
    phase = "username";
    cells = ["", "", "", "", "", ""];
    clearError();
  }

  onDestroy(() => stopResendCountdown());
</script>

<section
  class="page-login"
  data-testid="page-login"
  aria-labelledby="page-login-heading"
>
  <div class="page-login__card" role="region">
    <h1 id="page-login-heading" class="page-login__title">rust-dashboard</h1>
    <p class="page-login__subtitle">
      Sign in with your username. A 6-digit code is delivered to the
      operator's Telegram chat — type it below to mint a session cookie.
    </p>

    {#if nextPath}
      <p class="page-login__next" data-testid="page-login-next">
        After sign-in you'll return to <code>{nextPath}</code>.
      </p>
    {/if}

    {#if phase === "username" || phase === "sending"}
      <form
        class="page-login__form"
        data-testid="page-login-username-form"
        on:submit|preventDefault={sendCode}
      >
        <label class="page-login__field">
          <span>Username</span>
          <input
            type="text"
            autocomplete="username"
            inputmode="text"
            bind:value={username}
            on:input={onUsernameInput}
            data-testid="login-username"
            aria-invalid={!usernameValid && username.length > 0}
            disabled={submitting}
          />
        </label>
        <button
          type="submit"
          class="page-login__submit"
          disabled={!usernameValid || submitting}
          data-testid="login-send-code"
        >
          {phase === "sending" ? "Sending…" : "Send code"}
        </button>
      </form>
    {:else}
      <form
        class="page-login__form"
        data-testid="page-login-code-form"
        on:submit|preventDefault={verifyCode}
      >
        <p class="page-login__muted">
          Code sent to <strong>{username}</strong>'s Telegram. Enter the
          6 digits below.
        </p>
        <div
          class="otp-row"
          role="group"
          aria-label="Six-digit verification code"
          data-testid="login-otp"
          on:paste={onOtpPaste}
        >
          {#each cells as cell, i (i)}
            <input
              type="text"
              inputmode="numeric"
              autocomplete="one-time-code"
              pattern="\d{1}"
              maxlength="1"
              class="otp-cell"
              aria-label={`Code digit ${i + 1} of 6`}
              value={cell}
              on:input={(e) => onOtpInput(i, e)}
              on:keydown={(e) => onOtpKeydown(i, e)}
              disabled={submitting || locked}
              data-testid={`login-otp-cell-${i}`}
            />
          {/each}
        </div>
        <div class="page-login__actions">
          <button
            type="button"
            class="page-login__ghost"
            on:click={back}
            disabled={submitting}
            data-testid="login-back"
          >
            Use a different username
          </button>
          <button
            type="button"
            class="page-login__ghost"
            on:click={onResend}
            disabled={resendIn > 0 || submitting}
            data-testid="login-resend"
          >
            {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend code"}
          </button>
          <button
            type="submit"
            class="page-login__submit"
            disabled={!codeComplete || submitting || locked}
            data-testid="login-verify"
          >
            {phase === "verifying" ? "Verifying…" : "Verify"}
          </button>
        </div>
      </form>
    {/if}

    {#if locked}
      <div
        class="page-login__locked"
        data-testid="login-locked"
        role="status"
        aria-live="polite"
      >
        Account locked — too many failed attempts. Try again in
        {lockedSeconds}s.
      </div>
    {/if}
    {#if errorMsg}
      <div
        class="page-login__error"
        data-testid="login-error"
        role="alert"
        aria-live="polite"
      >
        {errorMsg}
      </div>
    {/if}
  </div>
</section>

<style>
  .page-login {
    display: grid;
    place-items: center;
    min-height: 100vh;
    padding: var(--space-6);
    background: var(--bg);
  }
  .page-login__card {
    width: 420px;
    max-width: 100%;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius-md);
    padding: var(--space-7);
    display: grid;
    gap: var(--space-4);
  }
  .page-login__title {
    margin: 0;
    font: var(--type-h1);
    color: var(--text-strong);
  }
  .page-login__subtitle {
    margin: 0;
    color: var(--text-mid);
    font: var(--type-body-sm);
  }
  .page-login__next {
    margin: 0;
    background: var(--surface-2);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    padding: var(--space-3);
    color: var(--text-mid);
    font: var(--type-body-sm);
  }
  .page-login__next code {
    background: var(--surface);
    padding: 1px 6px;
    border-radius: 4px;
    color: var(--accent-cyan);
  }
  .page-login__form {
    display: grid;
    gap: var(--space-3);
  }
  .page-login__field {
    display: grid;
    gap: var(--space-2);
  }
  .page-login__field span {
    font: var(--type-label-caps);
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--text-mid);
  }
  .page-login__field input {
    background: var(--surface-2);
    color: var(--text-strong);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    padding: var(--space-3);
    font: var(--type-body);
  }
  .page-login__field input:focus-visible,
  .otp-cell:focus-visible {
    outline: 2px solid var(--accent-cyan);
    outline-offset: 1px;
    border-color: var(--accent-cyan);
  }
  .page-login__muted {
    margin: 0;
    color: var(--text-mid);
    font: var(--type-body-sm);
  }
  .otp-row {
    display: flex;
    gap: var(--space-2);
  }
  .otp-cell {
    width: 44px;
    height: 56px;
    text-align: center;
    font: var(--type-h2);
    font-variant-numeric: tabular-nums;
    background: var(--surface-2);
    color: var(--text-strong);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
  }
  .page-login__actions {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    justify-content: flex-end;
    flex-wrap: wrap;
  }
  .page-login__submit,
  .page-login__ghost {
    height: 36px;
    padding: 0 var(--space-5);
    border-radius: var(--radius-sm);
    border: 1px solid var(--border);
    cursor: pointer;
    font: var(--type-body);
    font-weight: 500;
  }
  .page-login__submit {
    background: var(--accent-cyan);
    color: var(--bg);
    border-color: var(--accent-cyan);
  }
  .page-login__submit:hover:not(:disabled) {
    background: #00C7B2;
    border-color: #00C7B2;
  }
  .page-login__ghost {
    background: transparent;
    color: var(--text-mid);
  }
  .page-login__ghost:hover:not(:disabled) {
    background: var(--surface-2);
    color: var(--text-strong);
  }
  .page-login__submit:disabled,
  .page-login__ghost:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
  .page-login__error {
    padding: var(--space-3);
    background: var(--err-bg);
    color: var(--err-red);
    border: 1px solid rgba(239, 68, 68, 0.30);
    border-radius: var(--radius-sm);
    font: var(--type-body-sm);
  }
  .page-login__locked {
    padding: var(--space-3);
    background: var(--warn-bg);
    color: var(--warn-amber);
    border: 1px solid rgba(245, 158, 11, 0.30);
    border-radius: var(--radius-sm);
    font: var(--type-body-sm);
  }
</style>
